import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { scrollToSection } from "@/components/Navbar";
import { useLanguage } from "@/i18n/LanguageContext";
import { testimonialsData } from "@/i18n/testimonialsData";

const HERO_IMG = "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwYXJjaGl0ZWN0dXJlJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3ODgwNDU5Mjh8MA&ixlib=rb-4.1.0&q=85";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.16, delayChildren: 0.25 } },
};
const lineVariant = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const ref = useRef(null);
  const { t, lang } = useLanguage();
  const td = testimonialsData[lang] || testimonialsData.fr;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const lines = [
    { text: t("hero.line1"), cls: "text-paper" },
    { text: t("hero.line2"), cls: "text-paper" },
    { text: t("hero.line3"), cls: "text-brasslight italic" },
  ];

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen bg-midnight flex items-end overflow-hidden">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -top-[12%] h-[124%]">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-[0.22]" />
        <div className="absolute inset-0 bg-gradient-to-b from-midnight/60 via-midnight/40 to-midnight" />
      </motion.div>

      <div data-testid="hero-portrait-watermark" className="absolute right-0 top-0 bottom-0 w-[55%] hidden md:block pointer-events-none overflow-hidden">
        <img
          src="https://customer-assets-agu9un31.emergentagent.net/job_olena-gurova-pro/artifacts/sm7xp6y7_lolo%20v2.jpg"
          alt=""
          className="absolute right-0 top-1/2 -translate-y-1/2 h-[88%] object-cover object-top grayscale opacity-[0.13] mix-blend-luminosity [mask-image:linear-gradient(to_left,black_20%,transparent_85%)]"
        />
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
          data-testid="hero-eyebrow"
          className="text-[11px] uppercase tracking-[0.35em] text-brasslight/90 mb-10"
        >
          {t("hero.eyebrow")}
        </motion.p>

        <motion.h1 variants={container} initial="hidden" animate="show" data-testid="hero-title"
          className="font-serif font-light leading-[0.95] tracking-tight text-[13.5vw] sm:text-[10vw] lg:text-[7.5vw]">
          {lines.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span variants={lineVariant} className={`block ${l.cls}`}>
                {l.text}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-10"
        >
          <div className="flex flex-col gap-6">
            <div data-testid="hero-languages" className="font-serif text-lg md:text-xl text-paper/70 flex flex-wrap gap-x-6 gap-y-2">
              <span>Français</span><span className="text-brass">·</span>
              <span>English</span><span className="text-brass">·</span>
              <span>Русский</span><span className="text-brass">·</span>
              <span>Українська</span>
            </div>
            <a
              href="https://www.pagesjaunes.fr/pros/52603125#blocAvis"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-rating"
              className="group flex items-center gap-3 w-fit"
            >
              <span className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} className="text-brasslight" fill="currentColor" strokeWidth={0} />
                ))}
              </span>
              <span className="text-[11px] uppercase tracking-[0.2em] text-paper/50 group-hover:text-brasslight transition-colors duration-300">
                5/5 · {td.ratingLabel}
              </span>
            </a>
          </div>
          <button
            data-testid="hero-cta-devis"
            onClick={() => scrollToSection("devis")}
            className="group inline-flex items-center gap-4 text-[11px] uppercase tracking-[0.25em] text-paper border border-paper/25 px-8 py-4 hover:border-brasslight hover:text-brasslight transition-colors duration-300 w-fit"
          >
            {t("hero.cta")}
            <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
